import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import { returnStringFormattedDateTime } from "@/lib/globalFormatterHelper";
import tradeDataStore from "@/store/tradeDataStore";
import { getFillFromStore } from "../../fillStateHelpers";
import { IOrderFill } from "@/lib/interfaces and Enums/InterfaceExecutionRequestQuoteLeg";

/**
 * Formats an ISO date string to "DD-Mon-YYYY HH:MM:SS" format
 * Wrapper around returnStringFormattedDateTime with null/invalid date handling
 *
 * @param isoString - ISO date string from backend
 * @returns Formatted date string with timestamp, or empty string if invalid
 */
export function formatFilledAtDateTime(
  isoString: string | null | undefined,
): string {
  if (!isoString) return "";

  const date = new Date(isoString);
  if (isNaN(date.getTime())) return "";

  return returnStringFormattedDateTime(isoString);
}

/**
 * Determines if the text should be styled
 *
 * @param fill
 * @param rawValue
 */

export function determinePendingText(
  fill: IOrderFill | null,
): boolean {
  if (fill?.fillType) {
    switch (fill.fillType) {
      case "New":
        return true;
      case "Updated":
        return true;
      case "Cancel":
        return true;
      case "Unchanged":
        return false;
      default:
        return false;
    }
  }
  return false;
}

/**
 * Determines the display value for a fill based on its state.
 * - For pending fills (New, Updated, Cancel): Shows the fillType status
 * - For unchanged fills: Shows the formatted filledAt timestamp
 *
 * @param fill - The fill object from store
 * @param rawValue - The raw value from params (fallback)
 * @returns The display value string
 */
export function determineFilledAtDisplayValue(
  fill: IOrderFill | null,
  rawValue?: string,
): string {
  if (fill?.fillType) {
    switch (fill.fillType) {
      case "New":
        return "New (Pending)";
      case "Updated":
        return "Updated (Pending)";
      case "Cancel":
        return "Cancel (Pending)";
      case "Unchanged":
        return formatFilledAtDateTime(fill.filledAt);
      default:
        return fill.fillType;
    }
  } else if (fill?.filledAt) {
    return formatFilledAtDateTime(fill.filledAt);
  }

  return rawValue ? formatFilledAtDateTime(rawValue) : "";
}

/**
 * AG Grid valueFormatter for copy/paste functionality
 * Returns the formatted date/time string for copying
 */
export function filledAtValueFormatter(params: any): string {
  if (!params.data) return "";

  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);
  const esid = fullTradeData?.esid;
  const blockId = params.data?.blockId;
  const fillId = params.data?.fillId;
  const extFillId = params.data?.fillData?.extFillId;

  const tradeData = tradeDataStore.getState().tradeData;

  const fill =
    esid && fillId !== undefined && blockId !== undefined
      ? getFillFromStore(tradeData, esid, blockId, fillId, extFillId)
      : null;

  return determineFilledAtDisplayValue(fill, params.value);
}
